import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StepIndicator } from "@/components/StepIndicator";
import { Calendar } from "@/components/ui/calendar";
import { useBooking } from "@/context/BookingContext";
import { TIME_SLOTS, humanSlot, SEASON, isInstallDay, slotTooSoon, LEAD_HOURS } from "@/data/collections";
import { getAvailability } from "@/lib/api";
import { toast } from "sonner";

const fmt = (d) => {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function Schedule() {
  const nav = useNavigate();
  const { booking, update } = useBooking();
  const [date, setDate] = useState(booking.date ? new Date(booking.date + "T12:00:00") : undefined);
  const [unavailable, setUnavailable] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!booking.collectionId) nav("/", { replace: true });
  }, [booking, nav]);

  useEffect(() => {
    const iso = fmt(date);
    if (!iso) return;
    setLoading(true);
    getAvailability(iso)
      .then((r) => setUnavailable(r.unavailable_slots || []))
      .catch(() => setUnavailable([]))
      .finally(() => setLoading(false));
  }, [date]);

  const handleContinue = () => {
    const iso = fmt(date);
    if (!iso) return toast.error("Please pick a date");
    if (!booking.timeSlot) return toast.error("Please pick a time slot");
    if (slotTooSoon(date, booking.timeSlot)) return toast.error(`Please choose a window at least ${LEAD_HOURS} hours from now`);
    update({ date: iso });
    nav("/contact");
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const defaultMonth = today > SEASON.start ? new Date(today.getFullYear(), today.getMonth(), 1) : SEASON.start;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <StepIndicator current={3} />

      <section className="pp-container pb-20 pt-4">
        <div className="max-w-2xl mb-10">
          <div className="pp-caption mb-3">Step three · Your window</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-light" style={{ color: "var(--pp-burgundy)" }}>
            Reserve your install window
          </h1>
          <p className="mt-3 leading-relaxed" style={{ color: "var(--pp-text-muted)" }} data-testid="schedule-subtext">
            Pick the two-hour window that suits you. You'll pay securely at the next step, and the moment payment clears the window is yours.
          </p>
          <p className="mt-2 text-xs" style={{ color: "var(--pp-text-muted)" }}>Season: {SEASON.label} · Monday–Saturday · 8am–8pm · Two-hour windows, a limited number each day. Windows open at least {LEAD_HOURS} hours from now. Late September and October are reserved early.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="border p-3 sm:p-5 inline-block" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }} data-testid="calendar-picker">
              <Calendar mode="single" selected={date}
                onSelect={(d) => { setDate(d); update({ timeSlot: null }); }}
                defaultMonth={date || defaultMonth} fromMonth={SEASON.start} toMonth={SEASON.end}
                disabled={(d) => !isInstallDay(d)} initialFocus />
            </div>
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-serif-display text-3xl mb-5" style={{ color: "var(--pp-burgundy)" }}>
              {date ? date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "Select a date"}
            </h2>
            {!date ? (
              <p className="text-sm" style={{ color: "var(--pp-text-muted)" }}>Pick any open date on the calendar to see available install windows.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {TIME_SLOTS.map((slot) => {
                  const tooSoon = slotTooSoon(date, slot);
                  const taken = unavailable.includes(slot) || tooSoon;
                  const active = booking.timeSlot === slot;
                  return (
                    <button key={slot} data-testid={`time-slot-${slot.replace(":", "")}`} data-too-soon={tooSoon || undefined}
                      onClick={() => update({ timeSlot: slot })} disabled={taken || loading}
                      className="py-5 text-center transition-colors disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: active ? "var(--pp-terracotta)" : taken ? "transparent" : "var(--pp-cream-card)",
                        border: `1px solid ${active ? "var(--pp-terracotta)" : "var(--pp-border)"}`,
                        color: active ? "var(--pp-parchment)" : taken ? "var(--pp-text-muted)" : "var(--pp-text)",
                        opacity: taken ? 0.5 : 1,
                        textDecoration: taken ? "line-through" : "none",
                      }}>
                      <div className="font-serif-display text-2xl">{humanSlot(slot)}</div>
                      <div className="pp-caption mt-1" style={{ color: active ? "#F6E6D4" : undefined }}>{tooSoon ? "Too soon" : taken ? "Reserved" : "Open"}</div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="mt-10">
              <button data-testid="step-continue-button" onClick={handleContinue} disabled={!date || !booking.timeSlot} className="pp-btn">
                Hold this window <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>
      <div className="flex-1" />
      <Footer />
    </div>
  );
}
